import React, { useState } from "react";

import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Done as DoneIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { apply } from "@/utils/functions";

import If from "../../../../../../components/layout/If";
import Spacer from "../../../../../../components/layout/Spacer";
import useBooleanToggle from "../../../../../../hooks/useBooleanToggle";
import { useActions } from "../ClientExercisesPage";

interface Props {
  coachId?: string;
  commentId: string;
}

const Root = withStyles(Box, {
  root: {
    display: "flex",
    flexDirection: "column",
    marginBlock: "16px",
  },
});

const Head = withStyles(Box, {
  root: {
    display: "flex",
    alignItems: "center",
  },
});

const Body = withStyles(Box, {
  root: {
    fontFamily: "Mulish",
    color: "#4C515A",
    fontSize: 14,
    fontWeight: 600,
    wordWrap: "break-word",
    wordBreak: "break-word",
  },
});

const AuthorImage = withStyles(Avatar, {
  root: {
    width: 40,
    height: 40,
  },
});

const AuthorContainer = withStyles(Box, {
  root: {
    display: "flex",
    flexDirection: "column",
  },
});

const AuthorNameText = withStyles(Typography, {
  root: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 1.125,
    color: colors.dark,
  },
});

const TimestampText = withStyles(Typography, {
  root: {
    fontSize: 10,
    fontWeight: 700,
  },
});

const BodyButton = withStyles(ButtonBase, (theme) => ({
  root: {
    justifyContent: "start",
    margin: theme.spacing(0, -2),
    textAlign: "start",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.04)",
    },
  },
}));

const CommentField = withStyles(TextField, (theme) => ({
  root: {
    width: "100%",
    "& .MuiInputBase-root": {
      fontSize: 14,
      lineHeight: "20px",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
}));

const ButtonsContainer = styled("div")({
  position: "absolute",
  right: 0,
  zIndex: 10,
});

const RectIconButton = withStyles(Button, {
  root: {
    borderRadius: 0,
  },
  startIcon: {
    marginRight: 0,
  },
});

const exerciseCommentFragment = graphql(`
  fragment ClientExerciseComment on ExerciseComment {
    id
    author {
      id
      profile {
        id
        fullName
        avatarUrl
      }
    }
    body
    date
    createdAt
    deletedAt
  }
`);

const ExerciseComment: React.FC<Props> = ({ coachId, commentId }) => {
  const dispatch = useActions();
  const [comment] = useFragment(
    exerciseCommentFragment,
    `ExerciseComment:${commentId}`
  );
  const [isEditing, { setTrue: setEditingTrue, setFalse: setEditingFalse }] =
    useBooleanToggle();
  const [
    isDeleteDialogVisible,
    { setTrue: openDeleteDialog, setFalse: closeDeleteDialog },
  ] = useBooleanToggle();
  const [updatedComment, setUpdatedComment] = useState("");

  const startEditing = () => {
    if (comment == null) return;
    setUpdatedComment(comment.body);
    setEditingTrue();
  };

  const endEditing = () => {
    setUpdatedComment("");
    setEditingFalse();
  };

  const updateComment = () => {
    if (comment == null) return;
    dispatch({
      type: "UPDATE_EXERCISE_COMMENT",
      payload: {
        commentId: comment.id,
        body: updatedComment,
        date: comment.date,
      },
    }).finally(endEditing);
  };

  const deleteMeal = () => {
    if (comment == null) return;
    closeDeleteDialog();
    dispatch({
      type: "DELETE_EXERCISE_COMMENT",
      payload: { commentId: comment.id },
    }).finally(endEditing);
  };

  const confirmDelete = () => {
    openDeleteDialog();
  };
  return (
    <Root>
      <Head>
        <AuthorImage
          alt={comment?.author.profile?.fullName}
          src={comment?.author.profile?.avatarUrl ?? undefined}
        />
        <Spacer pxHorizontal={12} />
        <AuthorContainer>
          <AuthorNameText>{comment?.author.profile?.fullName}</AuthorNameText>
          <Box height={4} />
          <TimestampText>
            {apply(comment?.createdAt, (datetime) =>
              format({ datetime }, "MM/dd/yyyy hh:mm a")
            )}
          </TimestampText>
        </AuthorContainer>
      </Head>

      <Spacer multiplierVertical={2} />

      <If
        condition={isEditing}
        then={
          <Box marginX={-2} position="relative">
            <CommentField
              autoFocus={true}
              value={updatedComment}
              onChange={(event) => setUpdatedComment(event.target.value)}
              size="small"
              multiline
              onKeyPress={(event) => {
                if (event.key === "Enter") updateComment();
              }}
            />
            <ButtonsContainer>
              <ButtonGroup
                color="primary"
                size="small"
                variant="contained"
                disableElevation
              >
                <RectIconButton
                  aria-label="cancel"
                  color="secondary"
                  startIcon={<DeleteOutlineIcon fontSize="inherit" />}
                  onClick={confirmDelete}
                />
                <RectIconButton
                  aria-label="cancel"
                  startIcon={<CloseIcon fontSize="inherit" />}
                  onClick={endEditing}
                />
                <RectIconButton
                  aria-label="update"
                  startIcon={<DoneIcon fontSize="inherit" />}
                  onClick={updateComment}
                />
              </ButtonGroup>
            </ButtonsContainer>
          </Box>
        }
        else={
          <BodyButton
            disabled={!!comment?.deletedAt || comment?.author.id !== coachId}
            onClick={startEditing}
          >
            <Box padding={2}>
              <Body
                sx={{
                  fontStyle: comment?.deletedAt ? "italic" : "normal",
                  opacity: comment?.deletedAt ? 0.5 : 1,
                }}
              >
                {comment?.body}
              </Body>
            </Box>
          </BodyButton>
        }
      />
      <Spacer multiplierVertical={4} />
      <Dialog
        open={isDeleteDialogVisible}
        onClose={closeDeleteDialog}
        aria-labelledby="confirm-delete-dialog-title"
        aria-describedby="confirm-delete-dialog-description"
      >
        <DialogTitle id="confirm-delete-dialog-title">
          Delete comment?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-dialog-description">
            This action is irreversible. Do you wish to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary" variant="text">
            CANCEL
          </Button>
          <Button onClick={deleteMeal} color="primary" variant="text" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default ExerciseComment;
